import { useMutation, useQueryClient } from '@tanstack/react-query';
import { importGuidelinesFromFiles } from '../api/guidelines.api';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import type { GuidelineResponseDto } from '@/lib/api/guidelines-client';
import { useTranslation } from 'react-i18next';
import { parseProblemDetail } from '@/shared/utils/problem-details';

export interface ImportGuidelinesParams {
  files: File[];
  folderId?: string;
}

/** Failures are not toasted: the caller shows them, since a format error belongs beside the format. */
export const useImportGuidelinesFromFiles = () => {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation<GuidelineResponseDto[], Error, ImportGuidelinesParams>({
    mutationFn: async ({ files, folderId }) => {
      if (!workspaceId) throw new Error(t('guidelines.workspaceIdIsRequired'));

      const result = await importGuidelinesFromFiles(files, workspaceId, { folderId }).catch(
        (error: unknown) => {
          throw new Error(
            parseProblemDetail(
              error,
              t('guidelines.useImportGuidelinesFromFiles.failedToImportGuidelines'),
            ).detail,
          );
        },
      );
      if (!result?.guidelines.length) {
        throw new Error(t('guidelines.useImportGuidelinesFromFiles.noGuidelinesImported'));
      }

      return result.guidelines;
    },
    onSuccess: (data, variables) => {
      if (variables.folderId) {
        void queryClient.invalidateQueries({
          queryKey: ['guidelines', 'folder', variables.folderId],
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['folders', workspaceId] });
      notificationService.showSuccess(
        t('guidelines.useImportGuidelinesFromFiles.guidelinesImported', {
          count: data.length,
        }),
        { anchorOrigin: { vertical: 'bottom', horizontal: 'center' } },
      );
    },
  });
};
